import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { AUTH_REDIRECT_EVENT, clearStoredAuth } from '../utils/authRedirect';
import { isPreviewMode } from '../utils/previewMode';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const previewUser = {
  id: 'preview-user',
  email: 'demo@fasttime.com',
  name: 'Alexandra Miller',
  isAdmin: false,
  isActive: true,
};

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Captured once, here, at session establishment -- not re-derived from
  // window.location.search on every render. The URL is mutable (ordinary
  // in-app navigation rewrites it without a `search`), so anything that
  // re-reads `isPreviewMode()` later gets the wrong answer the moment the
  // visitor clicks anywhere. Consumers read this flag from context instead.
  //
  // Invariant: previewMode is true only for a session that was established
  // as a preview session, and any session transition ends it. AuthProvider
  // never remounts across sign-out -> welcome -> log-in (every step is a
  // client-side navigate(), not a document load), so a value that is only
  // ever set to true and never explicitly cleared would stay true across
  // those transitions -- rendering a real, newly-authenticated user's
  // dashboard as the fabricated demo, with every real fetch skipped. Every
  // place that establishes or ends a session (login, signup, logout,
  // handleAuthRedirect) must reset this to false; the mount-time checkAuth
  // effect below is the only place allowed to set it true, and only via
  // isPreviewMode().
  const [previewMode, setPreviewMode] = useState(false);

  // Check if user is logged in on mount
  useEffect(() => {
    const handleAuthRedirect = () => {
      setUser(null);
      setPreviewMode(false);
      setLoading(false);
    };

    window.addEventListener(AUTH_REDIRECT_EVENT, handleAuthRedirect);

    const checkAuth = async () => {
      if (isPreviewMode()) {
        setUser(previewUser);
        setPreviewMode(true);
        setLoading(false);
        return;
      }

      const accessToken = localStorage.getItem('accessToken');
      const userData = localStorage.getItem('user');
      const refreshToken = localStorage.getItem('refreshToken');

      // If we have refresh token but no/invalid access token, try refresh first
      if (refreshToken && (!accessToken || !userData)) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          setUser(JSON.parse(localStorage.getItem('user')));
        } else {
          clearStoredAuth();
          setUser(null);
        }
        setLoading(false);
        return;
      }

      if (accessToken && userData) {
        try {
          // Verify token is still valid by fetching profile
          const response = await axios.get(`${API_URL}/auth/profile`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });

          if (response.data.success) {
            setUser(response.data.data);
          } else {
            clearStoredAuth();
            setUser(null);
          }
        } catch (error) {
          // On any 401, try to refresh token if we have one
          if (error.response?.status === 401 && localStorage.getItem('refreshToken')) {
            const refreshed = await refreshAccessToken();
            if (refreshed) {
              setUser(JSON.parse(localStorage.getItem('user')));
            } else {
              clearStoredAuth();
              setUser(null);
            }
          } else {
            clearStoredAuth();
            setUser(null);
          }
        }
      }

      setLoading(false);
    };

    checkAuth();

    return () => {
      window.removeEventListener(AUTH_REDIRECT_EVENT, handleAuthRedirect);
    };
  }, []);

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return false;

    try {
      const response = await axios.post(`${API_URL}/auth/refresh`, {
        refreshToken,
      });

      if (response.data.success) {
        const { accessToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);

        // Fetch updated user profile
        const profileResponse = await axios.get(`${API_URL}/auth/profile`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (profileResponse.data.success) {
          setUser(profileResponse.data.data);
          localStorage.setItem('user', JSON.stringify(profileResponse.data.data));
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, {
      email,
      password,
    });

    if (response.data.success) {
      const { user, accessToken, refreshToken } = response.data.data;

      // Store tokens and user data
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));

      // A real login is a session transition -- end any preview session,
      // even one established earlier in this same tab.
      setPreviewMode(false);
      setUser(user);
      return { user };
    } else {
      throw new Error(response.data.message || 'Login failed');
    }
  };

  const signup = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/signup`, userData);

    if (response.data.success) {
      const { user, accessToken, refreshToken } = response.data.data;

      // Store tokens and user data
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));

      // Same as login -- creating a real account is a session transition,
      // and must not inherit a preview session from earlier in this tab.
      setPreviewMode(false);
      setUser(user);
      return { user };
    } else {
      throw new Error(response.data.message || 'Signup failed');
    }
  };

  const logout = async () => {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');

    try {
      if (accessToken && refreshToken) {
        await axios.post(
          `${API_URL}/auth/logout`,
          { refreshToken },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearStoredAuth();
      setUser(null);
      setPreviewMode(false);
    }
  };

  const value = {
    user,
    login,
    signup,
    logout,
    loading,
    refreshAccessToken,
    previewMode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
